import { useNavigate } from 'react-router-dom';
import AORLogo from '../components/AORLogo';

const NAVY  = '#1B2B4B';
const ORANGE = '#E8732A';

const CASES = [
  {
    badge: 'Real Estate',
    headline: 'National Commercial Real Estate Investment Company',
    details: '$100M+ total insured value · Holdings across 5 states',
    challenge: 'This real estate investment firm, with holdings across five states, faced rising insurance renewal costs and needed a strategic solution to optimize their coverage and reduce expenses.',
    approach: "We enhanced the client's insurance program by improving their Statement of Values, performing windstorm modeling on all properties, and leveraging multiple brokers to secure the best deals across their portfolio.",
    resultStat: '$245K saved · 30% premium reduction',
    resultText: 'We saved the real estate firm $245K on their insurance renewal.',
  },
  {
    badge: 'Construction',
    headline: 'National Construction Company',
    details: '$180M revenue',
    challenge: 'The insured was paying a high premium of $1.4M annually and needed a strategy to reduce costs while maintaining comprehensive coverage.',
    approach: 'We conducted a loss-sensitive analysis of their portfolio, examining loss factors and direct costs. We restructured their portfolio and incorporated a captive insurance program to optimize coverage and reduce expenses.',
    resultStat: '$800K saved · 57% premium reduction',
    resultText: 'We reduced their premium by $800K, while helping the insured maximize tax liability through the captive insurance program.',
  },
  {
    badge: 'Assisted Living',
    headline: 'Assisted Living Facility',
    details: '5 locations · 300 bed capacity',
    challenge: "A 1.9 experience mod for workers' compensation, a 15% consent to rate, and over $250K in annual costs without receiving any dividend. They were also paying above baseline rates for Property and General Liability coverage.",
    approach: "Lowered the workers' compensation experience mod and implemented strategies. Reduced their total premium from $1.08M to $829K by optimizing their insurance portfolio.",
    resultStat: '$251K saved · 23% premium reduction',
    resultText: "We reduced their workers' compensation costs to $81K, delivered a $44K dividend, and saved them $251K in premium.",
  },
  {
    badge: 'Manufacturing',
    headline: 'Manufacturing Facility',
    details: '$50M revenue',
    challenge: 'The manufacturing facility was dissatisfied with their high insurance costs and limited coverage, particularly the lack of international coverage under their Excess and Surplus property policy.',
    approach: 'We conducted a consultative review of their insurance program and transitioned them from the Excess and Surplus market to a standard global carrier, providing more comprehensive global coverage.',
    resultStat: '$50K saved · 30% premium reduction',
    resultText: 'This change saved the facility $50K in premiums while ensuring global coverage for all of their locations.',
  },
  {
    badge: 'Apparel Distribution',
    headline: 'National Apparel Distributor',
    details: '$200M revenue',
    challenge: 'The apparel distributor needed to expand from Canada into the USA while integrating their international and domestic operations under one global insurance program, all while reducing high insurance costs.',
    approach: 'We collaborated with the client and national brokers to create a comprehensive global insurance plan using a single carrier, which successfully tied together both their international and domestic operations.',
    resultStat: '$250K saved · 30% premium reduction',
    resultText: 'Our solution reduced their overall insurance costs by over $250K, streamlining their coverage across borders.',
  },
  {
    badge: 'Fine Dining',
    headline: 'Fine Dining Restaurant Group',
    details: '$15M revenue · 3 U.S. locations',
    challenge: 'This fine dining restaurant had three U.S. locations, all on separate policies with different renewal dates, which complicated coverage and increased costs.',
    approach: 'We consolidated their separate policies into one master policy, providing coverage for all three locations and added essential protections like Active Shooter and Cyber Insurance.',
    resultStat: '$50K saved · 19% premium reduction',
    resultText: 'Their total premium was reduced from $260K to $210K, while securing more comprehensive coverage for all locations.',
  },
];

export default function ResultsPage() {
  const navigate = useNavigate();

  return (
    <div style={{fontFamily:'sans-serif',background:'#f8fafc',minHeight:'100vh'}}>
      <style>{`
        .rp-grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;max-width:980px;margin:0 auto;padding:48px 24px 64px;}
        .rp-card{background:#fff;border:1px solid #eaeaea;border-radius:12px;padding:28px;transition:box-shadow 0.2s ease;}
        .rp-card:hover{box-shadow:0 8px 32px rgba(0,0,0,0.10);}
        .rp-section-label{font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#aaa;margin-bottom:5px;}
        .rp-section-text{font-size:13px;color:#444;line-height:1.65;margin:0;}
        @media(max-width:768px){
          .rp-grid{grid-template-columns:1fr!important;padding:28px 16px 48px;}
          .rp-hero-stats{flex-direction:column!important;align-items:center!important;gap:12px!important;}
          .rp-hero-inner{padding:48px 20px 44px!important;}
          .rp-hero-h1{font-size:26px!important;}
          .rp-footer-inner{padding:48px 20px!important;}
          .rp-footer-btns{flex-direction:column!important;align-items:stretch!important;}
        }
      `}</style>

      {/* ── HERO HEADER ── */}
      <div style={{background:NAVY,width:'100%'}}>
        <div className="rp-hero-inner" style={{maxWidth:980,margin:'0 auto',padding:'56px 24px 52px',textAlign:'center'}}>
          <div style={{display:'inline-block',background:'rgba(255,255,255,0.95)',borderRadius:12,padding:'10px 22px',marginBottom:32,lineHeight:0}}>
            <AORLogo height={42}/>
          </div>
          <h1 className="rp-hero-h1" style={{fontSize:34,fontWeight:900,color:'#fff',margin:'0 0 14px',lineHeight:1.15,letterSpacing:'-0.02em'}}>
            Proven Results Across Every Industry
          </h1>
          <p style={{fontSize:16,color:'rgba(255,255,255,0.6)',margin:'0 auto 36px',maxWidth:480,lineHeight:1.7}}>
            Real clients. Real savings. Documented results.
          </p>
          <div className="rp-hero-stats" style={{display:'flex',justifyContent:'center',gap:16,flexWrap:'wrap'}}>
            {[
              {val:'$1.6M+',label:'Total Saved'},
              {val:'32%',label:'Avg. Reduction'},
              {val:'6',label:'Industries'},
            ].map((s,i)=>(
              <div key={i} style={{background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.15)',borderRadius:99,padding:'10px 24px',display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:16,fontWeight:900,color:ORANGE,letterSpacing:'-0.01em'}}>{s.val}</span>
                <span style={{fontSize:13,color:'rgba(255,255,255,0.65)',fontWeight:500}}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CASE STUDY CARDS ── */}
      <div className="rp-grid">
        {CASES.map((c,i)=>(
          <div key={i} className="rp-card">
            {/* Badge */}
            <span style={{display:'inline-block',background:NAVY,color:'#fff',fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:99,letterSpacing:'0.06em',textTransform:'uppercase',marginBottom:14}}>
              {c.badge}
            </span>
            {/* Headline */}
            <h2 style={{margin:'0 0 6px',fontSize:16,fontWeight:700,color:NAVY,lineHeight:1.3}}>
              {c.headline}
            </h2>
            {/* Details */}
            <p style={{margin:'0 0 20px',fontSize:12,color:'#888'}}>
              {c.details}
            </p>
            {/* Challenge */}
            <div style={{marginBottom:14}}>
              <div className="rp-section-label">Challenge</div>
              <p className="rp-section-text">{c.challenge}</p>
            </div>
            {/* Approach */}
            <div style={{marginBottom:20}}>
              <div className="rp-section-label">Approach</div>
              <p className="rp-section-text">{c.approach}</p>
            </div>
            {/* Result */}
            <div style={{borderTop:'1px solid #f0f0f0',paddingTop:16}}>
              <div className="rp-section-label">Result</div>
              <div style={{fontSize:18,fontWeight:800,color:ORANGE,letterSpacing:'-0.01em',margin:'4px 0 6px'}}>
                {c.resultStat}
              </div>
              <p className="rp-section-text">{c.resultText}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── FOOTER CTA ── */}
      <div style={{background:NAVY,width:'100%'}}>
        <div className="rp-footer-inner" style={{maxWidth:980,margin:'0 auto',padding:'60px 24px',textAlign:'center'}}>
          <h2 style={{fontSize:28,fontWeight:900,color:'#fff',margin:'0 0 12px',letterSpacing:'-0.02em'}}>
            Ready to see what AOR can save your business?
          </h2>
          <p style={{fontSize:15,color:'rgba(255,255,255,0.6)',margin:'0 0 36px',lineHeight:1.7}}>
            Get your free personalized analysis in under 3 minutes.
          </p>
          <div className="rp-footer-btns" style={{display:'flex',justifyContent:'center',gap:14,flexWrap:'wrap'}}>
            <button
              onClick={()=>navigate('/')}
              style={{padding:'14px 32px',background:ORANGE,color:'#fff',border:'none',borderRadius:10,fontSize:15,fontWeight:800,cursor:'pointer',letterSpacing:'-0.01em'}}>
              Start My Free Analysis →
            </button>
            <a
              href="https://calendly.com/branden-aorinsurance/30min"
              target="_blank"
              rel="noreferrer"
              style={{padding:'14px 32px',background:'transparent',color:'rgba(255,255,255,0.85)',border:'1.5px solid rgba(255,255,255,0.3)',borderRadius:10,fontSize:15,fontWeight:600,textDecoration:'none',display:'inline-flex',alignItems:'center'}}>
              Book a Meeting
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
